// The coding suggestion, beside the note it came from.
//
// THE RULE THIS FILE EXISTS TO KEEP: a suggestion never touches a claim. This
// panel reads what the model proposed and records what the person decided. The
// only way a code reaches the claim is `onAcceptDiagnosis` / `onAcceptProcedure`
// — handed down from the claim editor, fired by a person's click on Accept, and
// landing in the editor's own form, which the person then saves through the
// editor's own edit path. So the claim's edit log names the person, never the
// model, and closing the drawer without saving leaves the claim untouched.
//
// Every line must carry a verbatim quote from the note; selecting a line
// highlights that quote in the note beside it, which is how a biller checks the
// suggestion rather than trusting it. A line the model was unsure of never
// arrives: `validate_suggestion` drops it and says so in `flags`, which this
// renders.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dottedIcd, recordDecision, requestSuggestion } from "./api";

// CMS-1500 box 21 holds twelve. The editor enforces the same limit on its own
// "add a diagnosis" button; it is repeated here so Accept can EXPLAIN that the
// claim is full rather than silently doing nothing.
const MAX_CLAIM_ICDS = 12;

// What a line reads as once a person has decided about it.
const DECIDED = {
  accepted: "Accepted",
  edited: "Accepted with an edit",
  rejected: "Rejected",
};

function lineLabel(line, isProcedure) {
  if (!isProcedure) return line.code;
  const mods = line.modifiers.length ? `-${line.modifiers.join("-")}` : "";
  return `${line.code}${mods}${line.units > 1 ? ` ×${line.units}` : ""}`;
}

export default function SuggestPanel({
  practiceId,
  claimId,
  // The note on screen. The suggestion is about THIS note, so switching tab
  // switches what a new suggestion would read.
  visitNote,
  // Every note on the claim, so the panel can NAME the one the server coded
  // rather than assume it is the one on screen.
  notes = [],
  features = {},
  // False when the payer has already seen the claim: there is nothing to accept
  // a code into, and offering the button would be a lie.
  canEdit = true,
  // THE SUGGESTION THIS CLAIM ALREADY HAS, and what the biller already decided
  // about it, from the notes GET that drew this pane. Null when nobody has asked.
  //
  // Without it the panel opened empty after every reload, and the only route back
  // to a suggestion Atlas already had was the button that spends — which returned
  // the cached row with each decision rendered as if nobody had made it, inviting
  // the biller to decide it again over a `decision_detail` that said otherwise
  // (staging 2026-09-17, production 2026-09-20).
  initialSuggestion = null,
  // What is already on the claim. Only ever READ, to say why a code cannot be
  // accepted — this panel never writes to the claim itself.
  claimIcds = [],
  claimLines = [],
  // What a suggestion costs, as the server words it. Shown BEFORE the click
  // that spends it — a cost line that only appears after the charge is not a
  // cost line. Never computed here: the price is the server's to say.
  cost = null,
  selectedKey = null,
  onSelect,
  onAcceptDiagnosis,
  onAcceptProcedure,
}) {
  const on = features.coding_assist === true;

  // Seeded from the prop, not fetched here: the notes GET has already paid for
  // this round trip, and a second one would paint the panel empty first.
  const [suggestion, setSuggestion] = useState(initialSuggestion);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState(null);
  // A 409 is not a failure. `coding_assist_service` raises AssistRefused with a
  // fact about this note or this claim — unsigned, no assessment, no matched
  // patient — each written to be read by a biller and each naming what to do
  // next. The route separates that from a 502, where something actually broke
  // and the message is deliberately generic. Painting a refusal red says Atlas
  // malfunctioned when it did exactly its job, and with `is_signed` false for
  // every source that sends no signature, refusal is the COMMON path.
  const [refused, setRefused] = useState(null);
  // key -> {decision, code, modifiers, units}. Seeded from what the server stored,
  // in the same shape `record()` writes, so the round trip is this panel's own.
  const [decisions, setDecisions] = useState(() =>
    initialSuggestion ? { ...initialSuggestion.decisions } : {},
  );
  // key currently being edited -> the text in its box
  const [editing, setEditing] = useState({});
  const recordQueue = useRef(Promise.resolve());
  // KEYS THIS PERSON HAS DECIDED, THIS SITTING. Seeding the panel from the stored
  // decisions means `decisions` now holds other people's verdicts too, and `record()`
  // posts every line it holds — so without this, biller B rejecting one line also
  // resends biller A's accept. The server no longer re-attributes an unchanged line,
  // which is the guarantee; this keeps the wire honest as well, so the audit record
  // of a post is what that post's author actually did.
  const touched = useRef(new Set());
  // ONCE. NotesPane renders this panel only after its load has settled, so the
  // prop is normally there at mount and the initialisers above have done the work.
  // This covers the prop arriving late, and never fires again: adopting a second
  // time would throw away a decision the biller has made since.
  const seeded = useRef(!!initialSuggestion);

  useEffect(() => {
    if (seeded.current || !initialSuggestion) return;
    seeded.current = true;
    setSuggestion(initialSuggestion);
    setDecisions({ ...initialSuggestion.decisions });
  }, [initialSuggestion]);

  // Which note the SERVER coded from. It picks (`note_to_code_from`), and on a
  // claim whose first tab is an unsigned draft or a plan of care that is often
  // not the tab the biller is looking at. The panel used to compare the two and
  // render nothing when they differed — a paid call, a blank panel and no
  // explanation. It names the note instead, and keeps rendering: a suggestion
  // about another document is still a suggestion, as long as nobody is misled
  // about which document it is about.
  const codedFrom = useMemo(() => {
    if (!suggestion || suggestion.visitNoteId == null) return null;
    return (notes || []).find((n) => n.id === suggestion.visitNoteId) || null;
  }, [suggestion, notes]);

  const codedElsewhere =
    !!suggestion &&
    !!visitNote &&
    suggestion.visitNoteId != null &&
    suggestion.visitNoteId !== visitNote.id;

  const lines = useMemo(() => {
    if (!suggestion) return [];
    // NO quote re-check here. `coding_assist_service` already validates every
    // quote and DROPS the line if it fails, so a line that arrives has passed —
    // presence in this list is the verdict. Re-checking was worse than
    // redundant: the server validates against the REDACTED text the model was
    // actually given, and this checked against the unredacted note, so any
    // sentence containing a patient name came back with a placeholder, failed,
    // and was shown to the biller as a fabricated citation with Accept withheld.
    // It blamed the model for quoting exactly what we gave it, and it got worse
    // the better the redactor became.
    return [
      ...suggestion.diagnoses.map((l) => ({ ...l, isProcedure: false })),
      ...suggestion.procedures.map((l) => ({ ...l, isProcedure: true })),
    ];
  }, [suggestion]);

  const ask = async () => {
    // No price, no run. The button is disabled without one, and this is the floor
    // under that: "may we spend on this note" is the BACKEND's question to answer,
    // and a null cost is its answer being no. Never re-derived here, never
    // overridden by asking harder.
    if (!on || !visitNote || asking || !costLine) return;
    setAsking(true);
    setError(null);
    setRefused(null);
    try {
      const next = await requestSuggestion(practiceId, claimId);
      // Whatever the server just handed back is now the truth about this panel;
      // a prop arriving afterwards must not overwrite it.
      seeded.current = true;
      // Only a DIFFERENT row starts a fresh sheet. The service caches on
      // `input_hash`, so pressing Suggest again on an unchanged note returns the
      // same row — and clearing the decisions then would re-arm every line the
      // biller had already accepted, and post a picture missing them.
      if (!suggestion || suggestion.id !== next.id) {
        // A DIFFERENT row still starts from what the server stored about IT —
        // normally nothing, and on a re-ask after a reload exactly the decisions
        // that row already carries. `{}` here would blank a decided row again.
        setDecisions({ ...next.decisions });
        setEditing({});
      }
      setSuggestion(next);
    } catch (err) {
      if (err.status === 409) setRefused(err.message);
      else setError(err.message);
    } finally {
      setAsking(false);
    }
  };

  // Every decision is written against the suggestion row, with the full current
  // picture rather than a delta, so a half-finished review is still an honest
  // record of what the person had decided when they walked away.
  const record = useCallback(
    (next) => {
      if (!suggestion || suggestion.id == null) return;
      const detail = lines
        .filter((line) => next[line.key] && touched.current.has(line.key))
        .map((line) => ({
          key: line.key,
          kind: line.isProcedure ? "procedure" : "diagnosis",
          suggested: line.code,
          ...next[line.key],
        }));
      const kinds = detail.map((d) => d.decision);
      const overall = kinds.includes("edited")
        ? "edited"
        : kinds.includes("accepted")
          ? "accepted"
          : kinds.length
            ? "rejected"
            : null;
      // Queued, never raced. Each call carries the whole picture rather than a
      // delta, so two in flight at once can land out of order and persist the
      // older one — losing a decision from the audit with nothing to notice it.
      recordQueue.current = recordQueue.current
        .then(() => recordDecision(practiceId, suggestion.id, overall, detail))
        .catch((err) => setError(err.message));
    },
    [lines, practiceId, suggestion],
  );

  const decide = (line, decision, code) => {
    // Through the same canonical form as everything else. An Edit box is free
    // text: a biller typing `F411` must put `F41.1` on the claim, or the code
    // they accepted and the code the claim carries are different strings.
    const chosen = canonical(line, code == null ? line.code : code);
    // Never fall back to the model's code. Clearing the Edit box and clicking
    // Accept used to put the model's own code on the claim and log it as the
    // biller's edit. Accept is blocked on an empty box, so this is the floor
    // under that, not the check the person sees.
    if (decision !== "rejected" && !chosen) return;
    const entry = line.isProcedure
      ? { decision, code: chosen, modifiers: line.modifiers, units: line.units }
      : { decision, code: chosen };
    const next = { ...decisions, [line.key]: entry };
    touched.current.add(line.key);
    setDecisions(next);
    setEditing((prev) => {
      const copy = { ...prev };
      delete copy[line.key];
      return copy;
    });
    setError(null);
    if (decision !== "rejected") {
      // The claim edit happens first and locally; the log write is what may
      // fail, and its failure must not look like the code was not accepted.
      if (line.isProcedure && onAcceptProcedure) {
        onAcceptProcedure({
          code: chosen,
          modifiers: line.modifiers,
          units: line.units,
        });
      } else if (!line.isProcedure && onAcceptDiagnosis) {
        onAcceptDiagnosis(chosen);
      }
    }
    record(next);
  };

  const costLine = cost;

  // ONE canonical form, applied at every point a code enters — the suggestion,
  // the claim, and whatever a biller types into the Edit box. Comparing two
  // spellings hopefully is what the dotted/dotless duplicate was in the first
  // place: `normalizeLine` dotted the suggestion while this side only trimmed
  // and upper-cased, so a claim carrying a DOTLESS code — exactly what the
  // assist stored before this PR — matched nothing and the same diagnosis could
  // be offered and accepted twice. A guard with a hole on the commonest
  // historical spelling does not stop anything reaching `claim_transformer`.
  //
  // The procedure exclusion is HERE, on `line.isProcedure`, and not inside
  // `dottedIcd`: shape cannot tell a HCPCS code from an ICD-10 one (`G2212` and
  // `G22.12`), so only a caller knows which kind it is holding. See dottedIcd.
  const canonical = (line, value) => {
    const raw = String(value == null ? "" : value).trim().toUpperCase();
    return line && line.isProcedure ? raw : dottedIcd(raw);
  };

  const onClaim = useMemo(
    () =>
      new Set(
        claimIcds
          .map((c) => dottedIcd(String(c || "").trim().toUpperCase()))
          .filter(Boolean),
      ),
    [claimIcds],
  );

  // A service line is the same line when its code and its modifiers match.
  const lineKey = (code, modifiers) =>
    `${code}|${[...(modifiers || [])].map((m) => String(m).toUpperCase()).sort().join(",")}`;

  const onClaimLines = useMemo(
    () =>
      new Set(
        (claimLines || [])
          .filter((l) => l && l.code)
          .map((l) => lineKey(String(l.code).trim().toUpperCase(), l.modifiers)),
      ),
    [claimLines],
  );

  // Whether what was accepted is still there. The editor owns the claim, and a
  // person can remove a code after accepting it.
  const stillOnClaim = (decided, line) =>
    line.isProcedure
      ? onClaimLines.has(lineKey(decided.code, decided.modifiers))
      : onClaim.has(decided.code);

  // Why this line cannot go onto the claim, or null. Said out loud rather than
  // handled by a disabled button with no explanation: a biller who clicks
  // Accept and sees nothing happen has been told the wrong thing.
  const blocked = (line, code) => {
    if (!canEdit) return "This claim cannot be edited.";
    if (!code) return "Enter a code to accept.";
    if (line.isProcedure) {
      // Two lines proposing the same code and modifiers, or a line the claim
      // already carries, would bill the same service twice.
      const key = lineKey(code, line.modifiers);
      const accepted = Object.entries(decisions).some(
        ([k, d]) =>
          k !== line.key &&
          d.decision !== "rejected" &&
          lineKey(d.code, d.modifiers) === key,
      );
      if (onClaimLines.has(key) || accepted) return "Already on the claim.";
      return null;
    }
    // Symmetric with the procedure path above. `claimIcds` makes this safe today
    // only because ClaimEditor passes the live form, and the prop defaults to
    // `[]` — a host that did not pass it would offer the same diagnosis twice.
    const acceptedHere = Object.entries(decisions).some(
      ([k, d]) => k !== line.key && d.decision !== "rejected" && d.code === code,
    );
    if (onClaim.has(code) || acceptedHere) return "Already on the claim.";
    // Counted the way the editor counts it (`form.icd_codes.length`), blanks and
    // all, so the two cannot disagree about when box 21 is full.
    if (claimIcds.length >= MAX_CLAIM_ICDS)
      return `The claim already has ${MAX_CLAIM_ICDS} diagnoses.`;
    return null;
  };

  return (
    <section className="nv-sg" aria-label="Coding suggestion">
      <div className="nv-sg-head">
        <h3 className="nv-sg-title">Suggest codes</h3>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={ask}
          disabled={!on || !visitNote || asking || !costLine}
        >
          {asking ? "Reading the note…" : "Suggest codes from this note"}
        </button>
        {costLine && <span className="nv-sg-cost">{costLine}</span>}
      </div>

      {!on && (
        <p className="nv-status">
          Coding assist is off for this practice. It is turned on for one practice
          at a time, on Practice Details.
        </p>
      )}
      {on && !visitNote && (
        <p className="nv-status">
          There is no note on screen to suggest from.
        </p>
      )}
      {on && visitNote && !costLine && (
        <p className="nv-status">
          This note cannot be priced, so Atlas will not suggest codes from it. A
          very long document is the usual reason.
        </p>
      )}
      {(!on || (visitNote && costLine && !suggestion && !asking)) && (
        <p className="nv-status">
          Reads the note above and proposes the diagnoses and procedures it
          supports, each with the sentence in the note it came from. You accept,
          edit or reject each one. Nothing reaches the claim until you do, and a
          code you accept is yours — it goes on as your edit, under your name.
        </p>
      )}

      {refused && <p className="nv-status nv-sg-refused">{refused}</p>}
      {error && (
        <div className="form-message error" role="alert">
          {error}
        </div>
      )}

      {suggestion && !canEdit && (
        <p className="nv-status">
          This claim cannot be edited, so a suggestion can be read but not
          accepted onto it.
        </p>
      )}

      {suggestion && lines.length === 0 && (
        <p className="nv-status">
          Nothing was suggested from this note.
        </p>
      )}

      {suggestion && (
        <p
          className={`nv-sg-coded${codedElsewhere ? " nv-sg-coded-other" : ""}`}
        >
          Coded from: {codedFrom ? codedFrom.docLabel : "the note Atlas chose"}
          {/* A title that only restates the document type adds nothing. */}
          {codedFrom &&
          codedFrom.title &&
          codedFrom.title.toLowerCase() !== (codedFrom.docLabel || "").toLowerCase()
            ? ` — ${codedFrom.title}`
            : ""}
          {codedElsewhere && " · not the document open above"}
        </p>
      )}

      {suggestion && lines.length > 0 && (
        <ul className="nv-sg-list">
          {lines.map((line) => {
            const decided = decisions[line.key];
            const isEditing = editing[line.key] !== undefined;
            const candidate = canonical(
              line,
              isEditing ? editing[line.key] : line.code,
            );
            const blockedReason = blocked(line, candidate);
            return (
              <li
                key={line.key}
                data-suggest-line={line.key}
                className={`nv-sg-line${selectedKey === line.key ? " selected" : ""}`}
              >
                <button
                  type="button"
                  className="nv-sg-pick"
                  onClick={() => onSelect && onSelect(line.key, line.quote)}
                  title="Show the sentence this came from"
                >
                  <span className="nv-sg-code">
                    {lineLabel(line, line.isProcedure)}
                  </span>
                  <span className="nv-sg-kind">
                    {line.isProcedure ? "procedure" : "diagnosis"}
                  </span>
                  {line.description && (
                    <span className="nv-sg-desc">{line.description}</span>
                  )}
                </button>

                {line.warning && (
                  <p className="nv-sg-warning">{line.warning}</p>
                )}
                {line.quote && (
                  <blockquote className="nv-sg-quote">{line.quote}</blockquote>
                )}

                {decided ? (
                  <p className="nv-sg-decided">
                    {DECIDED[decided.decision]}
                    {decided.decision !== "rejected" && decided.code !== line.code
                      ? ` — ${decided.code}`
                      : ""}
                    {decided.decision !== "rejected" &&
                      (stillOnClaim(decided, line) ? (
                        <span className="nv-sg-unsaved"> · save the claim to keep it</span>
                      ) : (
                        // The biller took it off again in the editor, or a save
                        // came back without it. Saying "Accepted" and nothing
                        // else would be a claim about the claim that is untrue.
                        <span className="nv-sg-unsaved"> · not on the claim now</span>
                      ))}
                  </p>
                ) : (
                  <div className="nv-sg-actions">
                    {isEditing && (
                      <input
                        className="nv-sg-edit"
                        value={editing[line.key]}
                        aria-label="Code to accept"
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [line.key]: e.target.value.toUpperCase(),
                          }))
                        }
                      />
                    )}
                    {blockedReason ? (
                      <span className="nv-sg-blocked">{blockedReason}</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() =>
                          decide(
                            line,
                            isEditing && editing[line.key] !== line.code
                              ? "edited"
                              : "accepted",
                            isEditing ? editing[line.key] : line.code,
                          )
                        }
                      >
                        Accept
                      </button>
                    )}
                    {canEdit && !isEditing && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() =>
                          setEditing((prev) => ({ ...prev, [line.key]: line.code }))
                        }
                      >
                        Edit
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => decide(line, "rejected")}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {suggestion && suggestion.flags.length > 0 && (
        <ul className="nv-sg-flags">
          {suggestion.flags.map((flag, i) => (
            <li key={i}>{flag}</li>
          ))}
        </ul>
      )}

      {suggestion && suggestion.model && (
        <p className="nv-sg-meta">
          {suggestion.model}
          {suggestion.promptVersion ? ` · prompt ${suggestion.promptVersion}` : ""}
        </p>
      )}
    </section>
  );
}
